import { createContext, useContext, useState, ReactNode } from 'react'
import apiClient, { getServerTimeouts } from '../api/client'

type GenerationStatus = 'idle' | 'generating' | 'success' | 'error'

interface GenerationResult {
  status: GenerationStatus
  previewUrl?: string | null
  previewUrls?: string[]
  metadata?: any
  progress?: number
  elapsedTime?: number
  message?: string | null
}

interface DesignerState {
  selectedModel: string | null
  selectedBodyPart: string | null
  selectedGarmentTransfer: string | null
  garmentMaskPrompt: string
  humanMaskPrompt: string
  // Unified selection fields used by DesignerPage
  garmentType: string | null
  garment: Record<string, string>
  studio: string | null
  quality: 'low' | 'high'
  modelImage: File | null
  modelImageUrl: string | null
  sourceImage: File | null
  sourceImageUrl: string | null
  positivePrompt: string
  transferMode: 'garment' | 'body' | 'specific'
  parentCategory?: string | null
  isCustomModel: boolean
  isGenerating: boolean
  results: any[]
  generationResult: GenerationResult
}

interface DesignerContextType extends DesignerState {
  // setters compatible with DesignerPage expectations
  setSelectedModel: (model: string | null) => void
  setModelType: (model: string | null) => void
  setSelectedBodyPart: (bodyPart: string | null) => void
  setBodyPart: (bodyPart: string | null) => void
  setSelectedGarmentTransfer: (transfer: string | null) => void
  setGarmentMaskPrompt: (prompt: string) => void
  setHumanMaskPrompt: (prompt: string) => void
  setQuality: (quality: 'low' | 'high') => void
  setModelImage: (image: string | File | null, isCustom?: boolean) => void
  setSourceImage: (image: string | File | null) => void
  setStudio: (studio: string | null) => void
  setGarmentType: (garmentType: string | null) => void
  setGarment: (key: string, path: string) => void
  setParentCategory: (parent: string | null) => void
  setTransferMode: (mode: 'garment' | 'body' | 'specific') => void
  setIsGenerating: (generating: boolean) => void
  setResults: (results: any[]) => void
  generateImageFromSelections: (projectId?: string) => Promise<boolean>
  resetState: () => void
  // convenient aggregate used in DesignerPage
  selections: {
    model?: string | null
    bodyPart?: string | null
    garmentType?: string | null
    garment: Record<string, string>
    studio?: string | null
    quality: 'low' | 'high'
    modelImage?: string | null
    sourceImage?: string | null
    isCustomModel?: boolean
    positivePrompt?: string
  }
}

const DesignerContext = createContext<DesignerContextType | undefined>(undefined)

export const useDesigner = () => {
  const context = useContext(DesignerContext)
  if (context === undefined) {
    throw new Error('useDesigner must be used within a DesignerProvider')
  }
  return context
}

interface DesignerProviderProps {
  children: ReactNode
}

const initialState: DesignerState = {
  selectedModel: null,
  selectedBodyPart: null,
  selectedGarmentTransfer: null,
  garmentMaskPrompt: '',
  humanMaskPrompt: '',
  garmentType: null,
  garment: {},
  studio: 'yacht',
  quality: 'low',
  modelImage: null,
  modelImageUrl: null,
  sourceImage: null,
  sourceImageUrl: null,
  positivePrompt: '',
  transferMode: 'garment',
  parentCategory: null,
  isCustomModel: false,
  isGenerating: false,
  results: [],
  generationResult: { status: 'idle', previewUrl: null, metadata: {} }
}

export const DesignerProvider = ({ children }: DesignerProviderProps) => {
  const [state, setState] = useState<DesignerState>(initialState)

  const setSelectedModel = (model: string | null) => {
    setState(prev => ({ ...prev, selectedModel: model }))
  }
  const setModelType = (model: string | null) => {
    setState(prev => ({ ...prev, selectedModel: model }))
  }

  const setSelectedBodyPart = (bodyPart: string | null) => {
    setState(prev => ({ ...prev, selectedBodyPart: bodyPart }))
  }
  // alias for compatibility with components expecting setBodyPart
  const setBodyPart = (bodyPart: string | null) => setSelectedBodyPart(bodyPart)

  const setSelectedGarmentTransfer = (transfer: string | null) => {
    setState(prev => ({ ...prev, selectedGarmentTransfer: transfer }))
  }

  const setGarmentMaskPrompt = (prompt: string) => {
    setState(prev => ({ ...prev, garmentMaskPrompt: prompt }))
  }

  const setHumanMaskPrompt = (prompt: string) => {
    setState(prev => ({ ...prev, humanMaskPrompt: prompt }))
  }

  const setQuality = (quality: 'low' | 'high') => {
    setState(prev => ({ ...prev, quality }))
  }

  const setModelImage = (image: string | File | null, isCustom?: boolean) => {
    if (typeof image === 'string') {
      setState(prev => ({ ...prev, modelImageUrl: image, isCustomModel: !!isCustom }))
    } else {
      setState(prev => ({ ...prev, modelImage: image, isCustomModel: !!isCustom }))
    }
  }

  const setSourceImage = (image: string | File | null) => {
    if (typeof image === 'string') {
      setState(prev => ({ ...prev, sourceImageUrl: image }))
    } else {
      setState(prev => ({ ...prev, sourceImage: image }))
    }
  }

  const setStudio = (studio: string | null) => {
    setState(prev => ({ ...prev, studio }))
  }

  const setGarmentType = (garmentType: string | null) => {
    setState(prev => ({ ...prev, garmentType }))
  }

  const setGarment = (key: string, path: string) => {
    setState(prev => ({ ...prev, garment: { ...prev.garment, [key]: path } }))
  }
  const setParentCategory = (parent: string | null) => {
    setState(prev => ({ ...prev, parentCategory: parent }))
  }

  const setTransferMode = (mode: 'garment' | 'body' | 'specific') => {
    setState(prev => ({ ...prev, transferMode: mode }))
  }

  const setIsGenerating = (generating: boolean) => {
    setState(prev => ({ ...prev, isGenerating: generating }))
  }

  const setResults = (results: any[]) => {
    setState(prev => ({ ...prev, results }))
  }

  const generateImageFromSelections = async (_projectId?: string): Promise<boolean> => {
    try {
      setIsGenerating(true)
      setState(prev => ({ ...prev, generationResult: { status: 'generating', progress: 5, message: 'Submitting job...' } }))

      // Resolve username from localStorage (AuthContext persists it there)
      const savedUser = localStorage.getItem('user')
      const username = savedUser ? (JSON.parse(savedUser).username as string) : ''
      if (!username) throw new Error('Not logged in')

      // Resolve project name
      const projectName = _projectId || 'jesien-zima-2025'

      // Determine selection semantics
      const type = state.garmentType || 'generic'
      const mode = state.transferMode || 'garment'
      const isMainCategory = ['generic', 'upper', 'lower', 'body'].includes(type)
      const requiresGarment = !isMainCategory && mode !== 'body'

      // Resolve garment path (only when required)
      const garmentPath = state.garment[type]
      if (requiresGarment && !garmentPath) throw new Error('No garment selected')

      // Resolve model image (always required)
      const modelImage = state.modelImageUrl || ''
      if (!modelImage) throw new Error('No model image selected')

      // Resolve source image when using main categories/body flow
      const sourceImage = state.sourceImageUrl || ''
      if (isMainCategory && !sourceImage) throw new Error('No source image selected')

      // Build payload
      const payload: any = {
        username,
        projectName,
        model: state.isCustomModel ? 'custom' : 'gallery',
        modelImage,
        studio: state.studio || 'real',
        garment: requiresGarment ? garmentPath : null,
        sourceImage: sourceImage || null,
        quality: state.quality,
        bodyPart: state.selectedBodyPart || 'full',
        garmentType: type,
        normalizedGarmentType: type,
        transferMode: mode,
        garmentMaskPrompt: state.garmentMaskPrompt || '',
        humanMaskPrompt: state.humanMaskPrompt || '',
        positivePrompt: state.positivePrompt || 'fashion photography'
      }

      // Determine request timeout from backend config (per quality)
      let timeoutMs = 300000 // default fallback
      try {
        const t = await getServerTimeouts()
        const isHigh = state.quality === 'high'
        const serverSec = isHigh ? t.high : t.low
        const bufferSec = isHigh ? 60 : 40 // safety margin to avoid axios abort before server finishes
        timeoutMs = (serverSec + bufferSec) * 1000
      } catch (_) {}

      // Async submit: queue job and poll status
      console.info('[Designer] Sending /api/generate_image_async payload', payload)
      const qresp = await apiClient.post('/generate_image_async', payload)
      const jobId: string | undefined = qresp?.data?.job_id
      if (!jobId) throw new Error('Failed to submit job')

      // Polling loop: try to fetch UI config for intervals
      let pollIntervalMs = 3000
      let maxWaitMs = timeoutMs // reuse computed timeout as upper bound
      try {
        const uic = await apiClient.get('/config/ui')
        const ui = uic?.data?.ui || {}
        if (typeof ui.poll_interval_seconds === 'number') pollIntervalMs = ui.poll_interval_seconds * 1000
        if (typeof ui.max_wait_seconds === 'number') maxWaitMs = ui.max_wait_seconds * 1000
      } catch {}
      const started = Date.now()
      console.info('[Designer] Queued job:', jobId)

      while (Date.now() - started < maxWaitMs) {
        await new Promise(r => setTimeout(r, pollIntervalMs))
        try {
          const st = await apiClient.get('/job_status', { params: { job_id: jobId, username, project: projectName } })
          const status = st?.data?.status
          if (status === 'ready') {
            const previews: string[] = st?.data?.result?.preview_files || []
            const pick = () => {
              const preferred = previews.find((p) => typeof p === 'string' && p.includes('output-preview-garment-and-human'))
              if (preferred) return preferred
              const images = previews.find((p) => typeof p === 'string' && p.includes('/images/'))
              if (images) return images
              return previews[0] || null
            }
            const previewUrl = pick()
            const allPreviews = previews.filter((p) => typeof p === 'string' && p.length > 0)
            setResults([{ id: Date.now(), imageUrl: previewUrl, metadata: { quality: state.quality } }])
            setState(prev => ({ ...prev, generationResult: { status: 'success', previewUrl, previewUrls: allPreviews, elapsedTime: Math.round((Date.now()-started)/1000), metadata: { quality: prev.quality } } }))
            return true
          } else if (status === 'failed') {
            throw new Error('Generation failed')
          }
        } catch (e) {
          // Keep polling; transient errors allowed
        }
      }
      throw new Error('Generation timeout (UI)')
    } catch (error) {
      console.error('Error generating image:', error)
      setState(prev => ({ ...prev, generationResult: { status: 'error', message: (error as Error).message } }))
      return false
    } finally {
      setIsGenerating(false)
    }
  }

  const resetState = () => {
    setState(initialState)
  }

  const value: DesignerContextType = {
    ...state,
    setSelectedModel,
    setModelType,
    setSelectedBodyPart,
    setBodyPart,
    setSelectedGarmentTransfer,
    setGarmentMaskPrompt,
    setHumanMaskPrompt,
    setQuality,
    setModelImage,
    setSourceImage,
    setStudio,
    setGarmentType,
    setGarment,
    setParentCategory,
    setTransferMode,
    setIsGenerating,
    setResults,
    generateImageFromSelections,
    resetState,
    selections: {
      model: state.selectedModel,
      bodyPart: state.selectedBodyPart,
      garmentType: state.garmentType,
      garment: state.garment,
      studio: state.studio,
      quality: state.quality,
      modelImage: state.modelImageUrl,
      sourceImage: state.sourceImageUrl,
      isCustomModel: state.isCustomModel,
      positivePrompt: state.positivePrompt
    }
  }

  return (
    <DesignerContext.Provider value={value}>
      {children}
    </DesignerContext.Provider>
  )
}
